import { makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import {
    areIntervalsOverlapping,
    differenceInCalendarDays,
    eachDayOfInterval,
    eachWeekOfInterval,
    endOfDay,
    endOfWeek,
    isSameMonth,
    isToday,
    startOfWeek,
} from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event } from './Event';
import { CalendarTrack, createTracks, denseArrangeTracks } from './util';
import { useMonthClasses as useYearMonthClasses } from './YearView';

const useMonthViewClasses = makeStyles({
    root: {
        display: 'grid',
        gridAutoRows: 'max-content',
        gridTemplateColumns: 'repeat(7, 1fr)',
        alignItems: 'start',
        overflow: 'auto',
        flex: '1 0 0',
        '&.drag-over > div:not(.dragging), &.drag-over > span > button': { pointerEvents: 'none' },
    },
    weekday: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        padding: 8,
        borderRight: `1px solid ${theme.colors.neutral[6]}`,
        borderBottom: `1px solid ${theme.colors.neutral[6]}`,
        color: theme.colors.neutral[2],
        backgroundColor: 'white',
        mixBlendMode: 'normal',
        ...theme.fonts.h6,
        textTransform: 'uppercase',
    },
    cell: {
        display: 'contents',
        '& > span': {
            alignSelf: 'stretch',
            minHeight: 120,
            borderRight: `1px solid ${theme.colors.neutral[6]}`,
            borderBottom: `1px solid ${theme.colors.neutral[6]}`,
            mixBlendMode: 'multiply',
        },
        '& > button': {
            position: 'sticky',
            top: 30,
            zIndex: 1,
            justifyContent: 'flex-start',
            padding: 8,
            borderRadius: 0,
            borderRight: `1px solid ${theme.colors.neutral[6]}`,
            mixBlendMode: 'normal',
            ...theme.fonts.h6,
            color: theme.colors.neutral[2],
            '& > div': { padding: 4, borderRadius: 2 },
        },
        '&.month > *': { backgroundColor: 'white' },
        '&:not(.month) > *': { background: theme.colors.neutral[4] },
        '&.now > button > div': { color: 'white', backgroundColor: theme.colors.blue[1] },
        '&.drag-over > button': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
    },
    event: {
        overflow: 'hidden',
        margin: '2px 8px',
        '&:not(.month)': { opacity: 0.4 },
        '&.first': { marginTop: 4 },
        '&.last': { marginBottom: 8 },
    },
});

/**
 * Calendar month view.
 *
 * @param props.context calendar context
 */
export const MonthView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useMonthViewClasses();
    const yearMonthClasses = useYearMonthClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.MonthView' });
    const { events, intervals, setView, setAnchor, onFilter, onSelected, onDnD, onExternalDnD } = React.useContext(
        props.context,
    );
    const weeksInterval = { start: startOfWeek(intervals.month.start), end: endOfWeek(intervals.month.end) };
    const weeksIntervals = eachWeekOfInterval(weeksInterval).map(start => ({ start, end: endOfWeek(start) }));
    const monthEvents = React.useMemo(
        () => events.filter(event => areIntervalsOverlapping(event.interval, weeksInterval)),
        [events, +weeksInterval.start, +weeksInterval.end],
    );
    const initialGridRow = 2;
    const weeksTracks = React.useMemo(
        () =>
            weeksIntervals
                .map(week => eachDayOfInterval(week).map(start => ({ start, end: endOfDay(start) })))
                .map(resolution => denseArrangeTracks(createTracks(monthEvents, resolution)))
                .reduce<{ tracks: CalendarTrack[]; levels: number; start: number; end: number }[]>((acc, tracks) => {
                    const previous = acc[acc.length - 1];
                    const start = !previous ? initialGridRow : previous.end + 1;
                    const end = start + tracks.levels;
                    acc.push({ ...tracks, start, end });
                    return acc;
                }, []),
        [monthEvents],
    );
    const dragEvent = React.useRef<CalendarEvent>();

    React.useEffect(() => onFilter?.(monthEvents), [onFilter, monthEvents]);

    const cells = weeksIntervals.flatMap((interval, i) => {
        const { start, end } = weeksTracks[i];
        return eachDayOfInterval(interval).map(day => {
            const buttonGridArea = `${start}/${1 + (day.getDay() % 7)}/auto/auto`;
            const cellGridArea = `${start}/${1 + (day.getDay() % 7)}/${end + 1}/auto`;
            const cls = { now: isToday(day), month: isSameMonth(day, intervals.month.start) };
            return (
                <span
                    key={`cell-${+day}`}
                    className={clsx(classes.cell, cls)}
                    onDragEnter={e =>
                        (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')
                    }
                    onDragOver={e => (dragEvent.current || onExternalDnD) && e.preventDefault()}
                    onDragLeave={e =>
                        e.target === e.currentTarget.children[0] && e.currentTarget.classList.remove('drag-over')
                    }
                    onDrop={e => {
                        e.currentTarget.classList.remove('drag-over');
                        if (!dragEvent.current) return onExternalDnD?.(e, day);
                        const difference = differenceInCalendarDays(day, dragEvent.current.interval.start);
                        if (difference !== 0) onDnD?.(dragEvent.current, { days: difference });
                    }}
                >
                    <span style={{ gridArea: cellGridArea }} />
                    <Tooltip classes={{ tooltip: yearMonthClasses.tooltip }} title={t('view-week')}>
                        <Button style={{ gridArea: buttonGridArea }} onClick={() => (setView('week'), setAnchor(day))}>
                            <div>{day.getDate()}</div>
                        </Button>
                    </Tooltip>
                </span>
            );
        });
    });

    const eventNodes = weeksTracks.flatMap(({ tracks, start, levels }, i) =>
        tracks.map((track, j) => {
            const event = track.events[0];
            const gridArea = `${start + track.level + 1}/${1 + track.ticks.start}/auto/${2 + track.ticks.end}`;
            const cls = {
                month: areIntervalsOverlapping(track.interval, intervals.month, { inclusive: true }),
                first: track.level === 0,
                last: track.level === levels - 1,
            };
            return (
                <Event
                    key={`event-${i * 1000000 + j}`}
                    className={clsx(classes.event, cls)}
                    style={{ gridArea }}
                    event={event}
                    view='month'
                    onClick={() => onSelected?.(event)}
                    draggable={event.draggable && !!onDnD}
                    onDragStart={e => ((dragEvent.current = event), e.currentTarget.classList.add('dragging'))}
                    onDragEnd={e => ((dragEvent.current = undefined), e.currentTarget.classList.remove('dragging'))}
                />
            );
        }),
    );

    return (
        <div
            className={clsx(classes.root, 'scroll-shadow')}
            style={{ ['--shadow-offset-t' as any]: '67px' }}
            onDragStart={e => (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
            onDragEnter={e => (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
            onDrop={e => e.currentTarget.classList.remove('drag-over')}
            onDragEnd={e => e.currentTarget.classList.remove('drag-over')}
        >
            {eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) }).map(day => (
                <span key={+day} className={classes.weekday} style={{ gridArea: `1/${1 + day.getDay()}/auto/auto` }}>
                    {t('day', { date: day })}
                </span>
            ))}
            {cells}
            {eventNodes}
        </div>
    );
};
